/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import styles from './styles';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, DeleteRounded, LogoutOutlined, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import { renderCell } from '../../Datagrid/table_config';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import ModalDialog from '../../ModalDialog';
import { columns, strippingTableColumns } from './table_config';
import { HYDRAULICS_TRIP_PIPE_TYPE, HYDRAULICS_TRIP_PIPE_TYPE_VALUE, MAX_ROW_LIMITS, STRIPPING_REPORT_EVERY_SI, STRIPPING_REPORT_EVERY_US } from '../../../../utils/constants';
import SelectEditValueTypes from '../../SelectEditValueTypes';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { addNewStrippingRow, createStrippings, deleteStrippings, listStripping, runStrippingAnalysis, updateStripping } from '../../../../store/actions/stripping';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import { COLORS } from '../../../../shared/config/colors';
function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}
const StrippingModal = ({
  onCloseClick,
  selectedJob,
  isOpen,
  rowData
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { unit, convertUnitSystem } = useUnitConversion();
  const hydraulics = useSelector((state) => state.hydraulics);
  const stripping = useSelector((state) => state.stripping);
  const depthPoints = hydraulics?.units?.run_response?.depth_points;
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [runDetails, setRundetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    if (rowData?.run_details) {
      const { drillStrings = [], drillFluids = [] } = hydraulics?.drillDetails?.hydraulicsDrillDetails || {};
      const drillString = drillStrings.find(item => item._id === rowData?.run_details.drillStringsId) || drillStrings[0];
      const drillFluid = drillFluids.find(item => item._id === rowData?.run_details.drillFluidsId) || drillFluids[0];
      const runDetails = {
        ...rowData.run_details,
        _id: generateRandom(),
        rpm: rowData?.run_details?.rpm ?? 0.0,
        bit_depth_offset: rowData?.run_details?.bit_depth_offset ?? 0.0,
        drillStringsId: drillString
          ? `${drillString.name.trim()} @ ${drillString.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : '',
        drillFluidsId: drillFluid
          ? `${drillFluid.name.trim()} @ ${drillFluid.MD.toFixed(hydraulics?.drillDetails?.units?.MD?.decimals)}${depthPoints?.md?.label}`
          : ''
      };
      setRundetails([runDetails]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!stripping.loading) {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listStripping(payload));
    }
  }, []);

  const getPipeConfig = (configValue) => {
    return Object.keys(HYDRAULICS_TRIP_PIPE_TYPE_VALUE).find(
      (key) => HYDRAULICS_TRIP_PIPE_TYPE_VALUE[key] === configValue
    ) || configValue;
  };

  useEffect(() => {
    if (stripping?.data) {
      const stripData = stripping.data.map((item) => {
        let report_interval;

        if (unit === 'SI') {
          report_interval = STRIPPING_REPORT_EVERY_SI.includes(item.report_interval)
            ? item.report_interval
            : 250;
        } else {
          report_interval = STRIPPING_REPORT_EVERY_US.includes(item.report_interval)
            ? item.report_interval
            : 500;
        }

        return {
          ...item,
          tubular: getPipeConfig(item.tubular),
          report_interval,
        };
      });
      setProperties(stripData);
    }
  }, [stripping?.data, unit]);

  useEffect(() => {
    if (loader) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader]);

  useEffect(() => {
    if (properties[0]?.from === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'from', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'from');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
    setProperties([]);
  };

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return 0;
    dispatch(addNewStrippingRow(unit));
  };

  const runHydraulicsProxy = (hydraulicsRunID) => {
    const body = {
      query: selectedJob?.fromWells
        ? {
            wellsInfoId: selectedJob?.wellsInfoId
          }
        : {
            jobId: selectedJob?._id
          },
      runId: hydraulicsRunID
    };
    dispatch(runStrippingAnalysis(body, () => {
      onCloseClick();
    }, () => {
      setLoader(false);
    }));
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {'Stripping'}
        </Grid>
        <Grid item>
          <IconButton
            disabled={properties.some(item => item.isDirty === true) || stripping.loading || stripping?.data[0]?.isNewRow || stripping?.data.length < 1}
            onClick={() => {
              runHydraulicsProxy(rowData?._id);
              setLoader(true);
            }}
          >
            <LogoutOutlined fontSize='large' />
          </IconButton>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: field === 'speed' || field === 'from' || field === 'to' || field === 'report_interval' ? Number(value) : (value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateStrippingDetails = (details) => {
    const { _id, speed, from, to } = details;
    const min_speed = stripping.units.inputs.speed.min;
    const max_speed = stripping.units.inputs.speed.max;
    const min_from = stripping.units.inputs.from.min;
    const min_to = stripping.units.inputs.to.min;
    const holeDepth = runDetails[0]?.hole_depth;
    const unitLabel = stripping?.units?.inputs?.to?.label;
    if (speed < min_speed || speed > max_speed) return showError(`Speed should be between ${min_speed} and ${max_speed} ${stripping?.units?.inputs?.speed?.label}`, _id);
    if (from < min_from || from > holeDepth) {
      return showError(
          `From MD should be between 0 and ${holeDepth} ${unitLabel}.`,
          _id
      );
    }

    if (to < min_to || to > holeDepth) {
      return showError(
          `To MD should be between 0 and ${holeDepth} ${unitLabel}.`,
          _id
      );
    }

    if (from === to) {
      return showError(
        'From MD and To MD should not be the same.',
        _id
      );
    }

    return true;
  };

  const checkStrippingValues = (value) => {
    const row = value;
    const invalidFields = [];
    const isInvalidNumber = (val) => {
      // eslint-disable-next-line no-mixed-operators
      return isNaN(val) || val === '' || typeof val === 'string' && val.trim() === '';
    };

    if (row?.from === undefined || row?.from === null || isInvalidNumber(row.from)) {
      invalidFields.push('From MD');
    }
    if (row?.to === undefined || row?.to === null || isInvalidNumber(row.to)) {
      invalidFields.push('To MD');
    }
    if (row?.speed === undefined || row?.speed === null || isInvalidNumber(row.speed)) {
      invalidFields.push('Speed');
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateStrippingDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
            `Please enter the following details: ${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveStripping = (row) => {
    if (checkStrippingValues(row)) {
      const body = {
        from: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.from, stripping?.units?.inputs?.from?.conversionFactor) : Number(row.from),
        to: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.to, stripping?.units?.inputs?.to?.conversionFactor) : Number(row.to),
        speed: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.speed, stripping?.units?.inputs?.speed?.conversionFactor) : Number(row.speed),
        report_interval: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.report_interval, stripping?.units?.inputs?.report_interval?.conversionFactor) : Number(row.report_interval),
        tubular: HYDRAULICS_TRIP_PIPE_TYPE_VALUE[row?.tubular]
      };
      const payload = {
        inputs: body,
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        ...(row?.isNewRow ? {} : { _id: row._id })
      };
      const hydraulicsBody = {
        isStrippingResult: rowData?.isStrippingResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      if (row?.isNewRow) {
        dispatch(createStrippings(payload, hydraulicsBody));
      } else {
        dispatch(updateStripping(payload, hydraulicsBody));
      }
    }
  };

  const removeStripping = (row) => {
    if (row?.isNewRow) {
      const updatedParams = properties.filter(item => item._id !== row?._id);
      setProperties(updatedParams);
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id
      };
      dispatch(listStripping(payload));
    } else {
      const payload = {
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData._id,
        _id: row._id
      };
      const hydraulicsBody = {
        isStrippingResult: rowData?.isStrippingResults,
        query: selectedJob?.fromWells
          ? {
              wellsInfoId: selectedJob?.wellsInfoId
            }
          : {
              jobId: selectedJob?._id
            },
      };
      dispatch(deleteStrippings(payload, hydraulicsBody));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || stripping.loading}
          onClick={() => {
            saveStripping(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={stripping.loading}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderTripPipeTypeCell = (params) => {
    return (
        <SelectEditValueTypes
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={HYDRAULICS_TRIP_PIPE_TYPE}
        />
    );
  };

  const renderReportEveryCell = (params) => {
    return (
        <SelectEditValueTypes
          {...params}
          enableSort={false}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={unit === UNIT_SYSTEM.US ? STRIPPING_REPORT_EVERY_US : STRIPPING_REPORT_EVERY_SI }
        />
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderTargetsEditAction = () => {
    const targetData = rowData?.run_details?.target_data;
    const isEmpty = !targetData || Object.keys(targetData).length === 0;
    const { target_ecd: targetECD, anchor_point: anchorPoint } = targetData || {};
    const unitLabel = hydraulics?.units?.target_data?.target_ecd?.label ?? '';

    return (
        <Grid container justifyContent="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              {isEmpty
                ? (
                <Typography variant="button" fontSize="1rem" fontWeight={900}>
                  None
                </Typography>
                  )
                : (
                <Box display="flex" flexDirection="column">
                  <Typography variant="button" fontSize="1rem" fontWeight={900}>
                    {targetECD} {unitLabel}
                  </Typography>
                  <Typography variant="button" fontSize="1rem" fontWeight={900}>
                    @ {anchorPoint}
                  </Typography>
                </Box>
                  )}
            </Box>
          </Grid>
        </Grid>
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={() => {
        onCloseClick();
        setProperties([]);
      }}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Grid item xs={12} marginBottom={'15px'}>
          <Datagrid
            data={runDetails}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
              },
            }}
            loading={false}
            getRowId={(row) => row._id}
            columns={columns(renderCell, hydraulics?.units?.run_details, unit, renderTargetsEditAction)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            autoheight
            height={'150px'}
            hideFooterPagination
            hideFooterSelectedRowCount
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item className={classes.customTool} justifyContent="space-between" >
          <Grid item>
            <Typography variant='button' fontWeight='700' onClick={handleAddParameter} className={ properties?.length === MAX_ROW_LIMITS.STRIPPING || properties.some(item => item.isDirty === true) ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.STRIPPING} entries are allowed)`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                textAlign: 'justify',
                pointerEvents: loader ? 'none' : 'auto',
                backgroundColor: loader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem !important',
                fontWeight: '900 !important'
              },
            }}
            loading={stripping.loading}
            columns={strippingTableColumns(renderValueCell, renderActionCell, stripping?.units?.inputs, unit, renderTripPipeTypeCell, renderReportEveryCell, renderCell)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            height={'50vh'}
            isRowSelectable={() => !loader}
            getRowClassName={(params) =>
              loader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
          />
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeStripping}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
StrippingModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  selectedJob: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.object
};
StrippingModal.defaultProps = {
  selectedJob: {}
};
export default StrippingModal;
